import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  Bell,
  Ban,
  CalendarClock,
  Car,
  CheckCircle2,
  Clock,
  Megaphone,
  RotateCcw,
  Undo2,
  XCircle,
} from 'lucide-react';
import type { AppNotification } from '../../../api/notifications';

export interface NotificationMetadata {
  spotUsageId?: string;
  spotId?: string;
  spotCode?: string;
  usageType?: string;
  startAt?: string;
  endAt?: string;
  requesterName?: string;
  editorName?: string;
  action?: string;
  licensePlate?: string;
  photoUrl?: string;
  reportId?: string;
  reporterName?: string;
  reporterFirebaseUid?: string;
}

export function parseNotificationMetadata(
  metadata?: string | null,
): NotificationMetadata | null {
  if (!metadata) return null;
  try {
    return JSON.parse(metadata) as NotificationMetadata;
  } catch {
    return null;
  }
}

export function getNotificationIcon(type: string): LucideIcon {
  switch (type) {
    case 'SPOT_USAGE_REQUEST':
      return Bell;
    case 'SPOT_USAGE_APPROVED':
      return CheckCircle2;
    case 'SPOT_USAGE_REJECTED':
      return XCircle;
    case 'SPOT_USAGE_CANCELLED':
      return Ban;
    case 'SPOT_USAGE_EXPIRED':
    case 'BULK_SPOT_REQUEST_EXPIRED':
      return Clock;
    case 'SPOT_USAGE_RELEASED':
      return Undo2;
    case 'SPOT_USAGE_RECLAIMED':
      return RotateCcw;
    case 'SPOT_MISUSE_REPORT':
      return AlertTriangle;
    case 'SPOT_MISUSE_RESOLVED':
      return CheckCircle2;
    case 'BULK_SPOT_REQUEST':
      return Megaphone;
    case 'SPOT_AVAILABLE':
      return Car;
    case 'AVAILABILITY_WINDOW_UPDATED':
      return CalendarClock;
    default:
      return Bell;
  }
}

export function getNotificationHref(notification: AppNotification): string | null {
  const meta = parseNotificationMetadata(notification.metadata);

  switch (notification.type) {
    case 'SPOT_USAGE_REQUEST': {
      if (meta?.spotId) {
        const params = new URLSearchParams({
          spotId: meta.spotId,
          openPendingRequests: 'true',
        });
        if (meta.spotUsageId) params.set('usageId', meta.spotUsageId);
        return `/my-parking-spots?${params.toString()}`;
      }
      return '/my-parking-spots';
    }
    case 'BULK_SPOT_REQUEST':
      if (meta?.spotId) {
        return `/my-parking-spots?spotId=${encodeURIComponent(meta.spotId)}&openAvailability=true`;
      }
      return '/my-parking-spots';
    case 'SPOT_USAGE_APPROVED':
    case 'SPOT_USAGE_REJECTED':
    case 'SPOT_USAGE_CANCELLED':
    case 'SPOT_USAGE_EXPIRED':
      return '/my-parking-spots';
    case 'SPOT_USAGE_RELEASED':
      if (meta?.spotId) {
        return `/my-parking-spots?spotId=${encodeURIComponent(meta.spotId)}`;
      }
      return '/my-parking-spots';
    case 'SPOT_USAGE_RECLAIMED':
      return '/my-spot-usages';
    case 'SPOT_MISUSE_REPORT':
    case 'SPOT_MISUSE_RESOLVED':
      return '/available-spots?tab=occupied';
    case 'SPOT_AVAILABLE':
      return '/available-spots';
    case 'AVAILABILITY_WINDOW_UPDATED': {
      if (meta?.spotId) {
        return `/my-parking-spots?spotId=${encodeURIComponent(meta.spotId)}`;
      }
      return '/my-parking-spots';
    }
    default:
      return null;
  }
}
